import 'moment/locale/el';
import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router';
import { usePlans } from '../api/hooks/usePlans';
import { Loading } from '../components/atoms/Loading';
import { UpgradeRequiredBanner } from '../components/atoms/UpgradeRequiredBanner';
import { STANDALONE_FEATURE, StandaloneFeatureType, SUBSCRIPTION } from '../interfaces/enumerables';
import { CustomerScreen } from '../screens/Customers/CustomerScreen';
import { CustomersScreen } from '../screens/Customers/CustomersScreen';
import { NewCustomerScreen } from '../screens/Customers/NewCustomerScreen';
import { DeliveryOptionScreen } from '../screens/Delivery/DeliveryOptionsScreen';
import { NewDeliveryOutcomeScreen } from '../screens/Delivery/NewDeliveryOutcomeScreen';
import { NewDeliveryRegisterTransferScreen } from '../screens/Delivery/NewDeliveryRegisterTransferScreen';
import { NewDeliveryReturnScreen } from '../screens/Delivery/NewDeliveryReturnScreen';
import { NewDeliveryScreen } from '../screens/Delivery/NewDeliveryScreen';
import { NewRejectDeliveryScreen } from '../screens/Delivery/NewRejectDeliveryScreen';
import { HomeScreen } from '../screens/Home/HomeScreen';
import { MoneyCollectionListScreen } from '../screens/MoneyCollection/MoneyCollectionListScreen';
import { MoneyCollectionScreen } from '../screens/MoneyCollection/MoneyCollectionScreen';
import { NewMoneyCollectionScreen } from '../screens/MoneyCollection/NewMoneyCollectionScreen';
import { NewProductScreen } from '../screens/Products/NewProductScreen';
import { ProductScreen } from '../screens/Products/ProductScreen';
import { ProductsScreen } from '../screens/Products/ProductsScreen';
import { ReceivedDocScreen } from '../screens/ReceivedDocs/ReceivedDocScreen';
import { ReceivedDocsScreen } from '../screens/ReceivedDocs/ReceivedDocsScreen';
import { CustomerReportScreen } from '../screens/Reports/CustomerReportScreen';
import { CustomersSubtotalsReportScreen } from '../screens/Reports/CustomersSubtotalsReportScreen';
import { ReportsScreen } from '../screens/Reports/ReportsScreen';
import { NewSaleScreen } from '../screens/Sales/NewSaleScreen';
import { SaleScreen } from '../screens/Sales/SaleScreen';
import { ViewDocByMarkScreen } from '../screens/Sales/ViewDocByMarkScreen';
import { SalesScreen } from '../screens/Sales/SalesScreen';
import { CompaniesScreen } from '../screens/Settings/Companies/CompaniesScreen';
import { CompanyScreen } from '../screens/Settings/Companies/CompanyScreen';
import { SettingsScreen } from '../screens/Settings/SettingsScreen';
import { selectSelectedCompany } from '../store/companies/companiesSlice';
import { PATHS } from './data';
import Navbar from './Navbar';

interface SubscriptionGateProps {
  planToCheck?: string;
  featureToCheck?: StandaloneFeatureType;
  children: React.ReactNode;
}

const SubscriptionGate: React.FC<SubscriptionGateProps> = ({ planToCheck, featureToCheck, children }) => {
  const company = useSelector(selectSelectedCompany);
  const { data, isLoading, isError } = usePlans(company?.vatNumber || '');

  // Server error → allow access (matches existing convention in useHasActiveFeature)
  if (isError) return <>{children}</>;

  // Block screen from mounting (and triggering API calls) until plans are resolved
  if (isLoading) return <Loading backgroundFull />;

  const planOk = !planToCheck || !!data?.activePlans?.[planToCheck];
  const featureOk = !featureToCheck || !!data?.activeFeatures?.[featureToCheck];

  if (!planOk || !featureOk) {
    return (
      <div className="width-100 flex-center-center-column-nowrap">
        <UpgradeRequiredBanner />
      </div>
    );
  }

  return <>{children}</>;
};

export const NavigationContainer = () => {
  return (
    <Navbar>
      <Routes>
        <Route path={PATHS.HOME} element={<HomeScreen />} />

        <Route path={PATHS.SALES} element={<SalesScreen />} />
        <Route path={`${PATHS.SALES}/view/:id`} element={<SaleScreen />} />
        <Route path={`${PATHS.SALES}/create`} element={<NewSaleScreen />} />
        <Route path={`${PATHS.SALES}/edit/:id`} element={<NewSaleScreen />} />
        <Route path={`${PATHS.SALES}/clone/:id`} element={<NewSaleScreen />} />
        <Route path={`${PATHS.SALES}/correlate/:id`} element={<NewSaleScreen />} />
        <Route path={`${PATHS.SALES_VIEW_BY_MARK}/:mark`} element={<ViewDocByMarkScreen />} />

        <Route path={PATHS.CUSTOMERS} element={<CustomersScreen />} />
        <Route path={`${PATHS.CUSTOMERS}/view/:id`} element={<CustomerScreen />} />
        <Route path={`${PATHS.CUSTOMERS}/create`} element={<NewCustomerScreen />} />
        <Route path={`${PATHS.CUSTOMERS}/:id`} element={<NewCustomerScreen />} />

        <Route path={PATHS.PRODUCTS} element={<ProductsScreen />} />
        <Route path={`${PATHS.PRODUCTS}/view/:id`} element={<ProductScreen />} />
        <Route path={`${PATHS.PRODUCTS}/clone/:id`} element={<NewProductScreen />} />
        <Route path={`${PATHS.PRODUCTS}/:id`} element={<NewProductScreen />} />

        <Route
          path={PATHS.MONEY_COLLECTION}
          element={
            <SubscriptionGate planToCheck={SUBSCRIPTION.SUBTOTALS}>
              <MoneyCollectionListScreen />
            </SubscriptionGate>
          }
        />
        <Route
          path={`${PATHS.MONEY_COLLECTION}/view/:id`}
          element={
            <SubscriptionGate planToCheck={SUBSCRIPTION.SUBTOTALS}>
              <MoneyCollectionScreen />
            </SubscriptionGate>
          }
        />
        <Route
          path={`${PATHS.MONEY_COLLECTION}/create`}
          element={
            <SubscriptionGate planToCheck={SUBSCRIPTION.SUBTOTALS}>
              <NewMoneyCollectionScreen />
            </SubscriptionGate>
          }
        />
        <Route
          path={`${PATHS.MONEY_COLLECTION}/:id`}
          element={
            <SubscriptionGate planToCheck={SUBSCRIPTION.SUBTOTALS}>
              <NewMoneyCollectionScreen />
            </SubscriptionGate>
          }
        />
        <Route
          path={`${PATHS.MONEY_COLLECTION}/clone/:id`}
          element={
            <SubscriptionGate planToCheck={SUBSCRIPTION.SUBTOTALS}>
              <NewMoneyCollectionScreen />
            </SubscriptionGate>
          }
        />

        <Route path={PATHS.REPORTS} element={<ReportsScreen />} />
        <Route path={`${PATHS.REPORTS}/customer`} element={<CustomerReportScreen />} />
        <Route path={`${PATHS.REPORTS}/customers`} element={<CustomersSubtotalsReportScreen />} />

        <Route
          path={`${PATHS.DELIVERIES}/register-transfer/:mark`}
          element={
            <SubscriptionGate featureToCheck={STANDALONE_FEATURE.TRANSIT}>
              <NewDeliveryRegisterTransferScreen />
            </SubscriptionGate>
          }
        />
        <Route
          path={`${PATHS.DELIVERIES}/confirm-outcome/:mark`}
          element={
            <SubscriptionGate featureToCheck={STANDALONE_FEATURE.TRANSIT}>
              <NewDeliveryOutcomeScreen />
            </SubscriptionGate>
          }
        />
        <Route
          path={`${PATHS.DELIVERIES}/reject-delivery/:mark`}
          element={
            <SubscriptionGate featureToCheck={STANDALONE_FEATURE.TRANSIT}>
              <NewRejectDeliveryScreen />
            </SubscriptionGate>
          }
        />
        <Route
          path={`${PATHS.DELIVERIES}/confirm-return/:mark`}
          element={
            <SubscriptionGate featureToCheck={STANDALONE_FEATURE.TRANSIT}>
              <NewDeliveryReturnScreen />
            </SubscriptionGate>
          }
        />
        <Route
          path={`${PATHS.DELIVERIES}/new`}
          element={
            <SubscriptionGate featureToCheck={STANDALONE_FEATURE.TRANSIT}>
              <NewDeliveryScreen />
            </SubscriptionGate>
          }
        />
        <Route
          path={`${PATHS.DELIVERIES}/:mark`}
          element={
            <SubscriptionGate featureToCheck={STANDALONE_FEATURE.TRANSIT}>
              <DeliveryOptionScreen />
            </SubscriptionGate>
          }
        />

        <Route
          path={PATHS.RECEIVED_DOCS}
          element={
            <SubscriptionGate planToCheck={SUBSCRIPTION.SUBTOTALS}>
              <ReceivedDocsScreen />
            </SubscriptionGate>
          }
        />
        <Route
          path={`${PATHS.RECEIVED_DOCS}/view/:mark`}
          element={
            <SubscriptionGate planToCheck={SUBSCRIPTION.SUBTOTALS}>
              <ReceivedDocScreen />
            </SubscriptionGate>
          }
        />

        <Route path={PATHS.COMPANIES} element={<CompaniesScreen />} />
        <Route path={`${PATHS.COMPANIES}/:id`} element={<CompanyScreen />} />
        <Route path={PATHS.SETTINGS} element={<SettingsScreen />} />

        <Route path="*" element={<Navigate to={PATHS.HOME} replace />} />
      </Routes>
    </Navbar>
  );
};
